// Hybrid Chatbot - Combines Smart Chatbot + Hugging Face AI
import { collection, getDocs } from "firebase/firestore";
import { db } from "@/firebase/config";

class HybridChatbot {
  constructor() {
    this.faqCache = null;
    this.currentLanguage = "en";
  }

  setLanguage(lang) {
    this.currentLanguage = lang;
  }

  async loadFAQData() {
    try {
      if (this.faqCache) return this.faqCache;

      console.log("Loading FAQ data from Firestore...");
      const faqSnapshot = await getDocs(collection(db, "faq"));
      this.faqCache = faqSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log(`Loaded ${this.faqCache.length} FAQs from Firestore`);
      return this.faqCache;
    } catch (error) {
      console.error("Error loading FAQ data:", error);
      return [];
    }
  }

  findFAQMatch(userMessage) {
    if (!this.faqCache) return null;

    const lowerMessage = userMessage.toLowerCase();

    for (const faq of this.faqCache) {
      // Check if any keyword matches
      const keywordMatch = faq.keywords.some((keyword) =>
        lowerMessage.includes(keyword.toLowerCase())
      );

      if (keywordMatch) {
        console.log(`Found FAQ match: ${faq.intent}`);
        return faq;
      }
    }

    return null;
  }

  getRandomVariation(faq) {
    if (!faq.variations || !faq.variations[this.currentLanguage]) {
      return faq.answer[this.currentLanguage] || faq.answer.en;
    }

    const variations = faq.variations[this.currentLanguage];
    const randomIndex = Math.floor(Math.random() * variations.length);
    return variations[randomIndex];
  }

  async generateResponse(userMessage) {
    try {
      console.log("🤖 Hybrid chatbot processing:", userMessage);

      // Step 1: Load FAQ data if not cached
      await this.loadFAQData();

      // Step 2: Check if question matches any FAQ
      const faqMatch = this.findFAQMatch(userMessage);

      if (faqMatch) {
        console.log("📝 Using FAQ response");
        const response = this.getRandomVariation(faqMatch);
        return response;
      }

      // Step 3: Use custom fallback response for unhandled queries
      console.log("🤖 No FAQ match, using custom fallback response...");

      // Step 4: Fallback to professional response
      console.log("📝 Using professional fallback response");
      if (this.currentLanguage === "ar") {
        return "🌟 **عذراً، يبدو أنك تبحث عن شيء غير محدد لمطعم توسكانى هاوس أو أنه عام جداً بالنسبة لي لفهمه.**\n\n💡 **يرجى تقديم المزيد من السياق للرد بشكل أفضل أو يمكنك الاتصال بنا عبر:**\n\n📞 **الهاتف:** +20-100-383-4294\n📧 **البريد الإلكتروني:** tuscany.house@gmail.com";
      }
      return "🌟 **I apologize, it seems like you're looking for something that is not specific to Tuscany House or it's too generic for me to understand.**\n\n💡 **Kindly provide me more context to respond well or you can contact us via:**\n\n📞 **Phone:** +20-100-383-4294\n📧 **Email:** tuscany.house@gmail.com";
    } catch (error) {
      console.error("❌ Hybrid chatbot error:", error);

      // Final fallback
      if (this.currentLanguage === "ar") {
        return "عذراً، حدث خطأ في معالجة طلبك. يرجى المحاولة مرة أخرى أو الاتصال بنا مباشرة.";
      }
      return "Sorry, there was an error processing your request. Please try again or contact us directly.";
    }
  }

  async refreshFAQData() {
    this.faqCache = null;
    await this.loadFAQData();
    console.log("FAQ data refreshed");
  }
}

// Create singleton instance
const hybridChatbot = new HybridChatbot();

// Export functions for use in components
export async function getHybridResponse(userMessage, language = null) {
  if (language) {
    hybridChatbot.setLanguage(language);
  }
  return await hybridChatbot.generateResponse(userMessage);
}

export function setHybridLanguage(language) {
  hybridChatbot.setLanguage(language);
}

export async function refreshHybridFAQData() {
  await hybridChatbot.refreshFAQData();
}

// Add missing functions that are imported in SmartChatbot.vue
export function clearSmartChatbotMemory() {
  // Clear conversation memory and cache
  hybridChatbot.faqCache = null;
  console.log("Smart chatbot memory cleared");
}

export async function refreshSmartChatbotData() {
  // Refresh FAQ data from Firestore
  await hybridChatbot.refreshFAQData();
  console.log("Smart chatbot data refreshed");
}

export default hybridChatbot;
